import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UpgradeService, type UpgradeEvent } from '../../services/upgradeService';
import { mercadoPagoService } from '../../services/api/mercadoPagoService';
import { subscriptionService } from '../../services/api/subscriptionService';
import { feedback } from '../../services/alertService';
import type { PlanListView } from '../../services/api/subscriptionService';

type Variant = 'trial-expired' | 'overdue' | 'feature-gate';

export const UpgradeModal: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [state, setState] = useState<UpgradeEvent>({ open: false });
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all plans for trial-expired or feature-gate variants
  const { data: allPlans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => subscriptionService.getPlans(),
    staleTime: 1000 * 60 * 5,
    enabled: state.open && !!state.businessId,
  });

  useEffect(() => {
    const sub = UpgradeService.subscribe((event) => {
      setState(event);
      setBillingCycle('monthly');
      setIsSubmitting(false);
    });
    return () => sub.unsubscribe();
  }, []);

  const handleClose = () => {
    setState({ open: false });
  };

  // ─── Determine variant ───
  const variant: Variant = state.featureName
    ? 'feature-gate'
    : state.isOverdue
      ? 'overdue'
      : 'trial-expired';

  // ─── Variant A: Trial Expired — subscribe to a plan ───
  const handleSubscribe = (planSlug: string) => {
    if (!state.businessId) return;
    handleClose();
    navigate(`/subscription/checkout?businessId=${state.businessId}&plan=${planSlug}&cycle=${billingCycle}`);
  };

  // ─── Variant B: Overdue — retry payment ───
  const handleRetry = async () => {
    if (!state.businessId) return;
    setIsSubmitting(true);
    try {
      const result = await mercadoPagoService.retrySubscription(state.businessId);
      window.location.href = result.checkoutUrl;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erro ao tentar pagamento.';
      feedback(message, 'error');
      setIsSubmitting(false);
    }
  };

  // ─── Variant C: Feature Gate — upgrade plan ───
  const handleUpgrade = async (planSlug: string) => {
    if (!state.businessId) return;
    setIsSubmitting(true);
    try {
      await mercadoPagoService.upgradePlan(state.businessId, {
        newPlanSlug: planSlug,
        newBillingCycle: billingCycle,
      });
      feedback(`Upgrade realizado! Você já pode usar "${state.featureName}".`, 'success');
      handleClose();
      // Reload page to unlock the feature
      window.location.reload();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erro ao fazer upgrade.';
      feedback(message, 'error');
      setIsSubmitting(false);
    }
  };

  // ─── Reusable plan cards ───
  const renderPlanCards = (
    plans: PlanListView[],
    actionLabel: string,
    onAction: (slug: string) => void,
    currentPlanSlug?: string,
  ) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
      {plans.map((plan) => {
        const price = billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
        const isCurrent = plan.slug === currentPlanSlug;

        return (
          <Card
            key={plan.slug}
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: isCurrent ? theme.palette.primary.main : undefined,
              borderWidth: isCurrent ? 2 : 1,
              opacity: isCurrent ? 0.7 : 1,
              borderRadius: 2,
              position: 'relative',
            }}
          >
            {plan.isPopular && !isCurrent && (
              <Chip
                label="Popular"
                color="primary"
                size="small"
                sx={{ position: 'absolute', top: -10, right: 12, fontWeight: 700 }}
              />
            )}
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {plan.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1 }}>
                <Typography variant="h4" fontWeight={700}>
                  {price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  /{billingCycle === 'yearly' ? 'ano' : 'mês'}
                </Typography>
              </Box>
              {billingCycle === 'yearly' && plan.yearlyDiscountPercentage != null && plan.yearlyDiscountPercentage > 0 && (
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Economia de {plan.yearlyDiscountPercentage}%
                </Typography>
              )}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button
                variant={isCurrent ? 'outlined' : 'contained'}
                fullWidth
                disabled={isCurrent || isSubmitting}
                onClick={() => onAction(plan.slug)}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                {isCurrent ? 'Plano Atual' : actionLabel}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </Box>
  );

  const renderBillingToggle = () => (
    <ToggleButtonGroup
      value={billingCycle}
      exclusive
      onChange={(_, val) => val && setBillingCycle(val)}
      size="small"
      sx={{ mt: 1 }}
    >
      <ToggleButton value="monthly" sx={{ borderRadius: '8px 0 0 8px', px: 3 }}>
        Mensal
      </ToggleButton>
      <ToggleButton value="yearly" sx={{ borderRadius: '0 8px 8px 0', px: 3 }}>
        Anual
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // ─── Render title, message, icon per variant ───
  const titleText = variant === 'feature-gate'
    ? 'Assinatura Insuficiente'
    : variant === 'overdue'
      ? 'Pagamento não confirmado'
      : 'Assinatura Expirada';

  const iconName = variant === 'feature-gate'
    ? 'workspace_premium'
    : variant === 'overdue'
      ? 'payment'
      : 'timer_off';

  const messageText = variant === 'feature-gate'
    ? `O recurso "${state.featureName}" não está disponível no seu plano atual.`
    : variant === 'overdue'
      ? 'Sua assinatura está pendente de pagamento. Tente novamente.'
      : state.message || 'Seu período de teste expirou. Escolha um plano para continuar.';

  return (
    <Dialog
      open={state.open}
      onClose={handleClose}
      maxWidth={variant === 'overdue' ? 'sm' : 'md'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Icon sx={{ fontSize: 64, color: theme.palette.warning.main, mb: 1 }}>
          {iconName}
        </Icon>
        <Typography variant="h5" fontWeight={700}>
          {titleText}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box textAlign="center" sx={{ py: 2 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {messageText}
          </Typography>

          {/* ── Variant A: Trial Expired — plan selector ── */}
          {variant === 'trial-expired' && (
            <>
              {renderBillingToggle()}
              {isLoadingPlans ? (
                <CircularProgress sx={{ mt: 3 }} />
              ) : allPlans && allPlans.length > 0 ? (
                renderPlanCards(allPlans, 'Assinar', handleSubscribe)
              ) : null}
            </>
          )}

          {/* ── Variant C: Feature Gate — upgrade cards ── */}
          {variant === 'feature-gate' && (
            <>
              {renderBillingToggle()}
              {isLoadingPlans ? (
                <CircularProgress sx={{ mt: 3 }} />
              ) : allPlans && allPlans.length > 0 ? (
                renderPlanCards(
                  allPlans,
                  'Fazer Upgrade',
                  handleUpgrade,
                  undefined, // current plan slug — we don't know it here
                )
              ) : null}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', flexDirection: 'column', gap: 1, pb: 2 }}>
        {/* ── Variant B: Overdue — retry button ── */}
        {variant === 'overdue' && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={isSubmitting}
            sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
            onClick={handleRetry}
          >
            {isSubmitting ? 'Aguarde...' : 'Tentar pagamento'}
          </Button>
        )}

        {/* Close / Continue button */}
        <Button
          variant={variant === 'overdue' ? 'text' : 'text'}
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {variant === 'feature-gate' ? 'Continuar com plano atual' : 'Fechar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
